import {
  useEffect, useMemo, useRef, useState, useCallback,
} from 'react'
import { getAlgoliaClient } from '../algoliaClientFactory'

// ─── Default shape ─────────────────────────────────────────────────────────────

const defaultIndexResult = {
  hits: [], hitCount: 0, hasMore: false, loadMore: () => {},
}

const makeEmptyIndexResults = () => ({
  repos: { ...defaultIndexResult },
  folders: { ...defaultIndexResult },
  // eslint-disable-next-line camelcase
  sets_questions: { ...defaultIndexResult },
})

// ─── AlgoliaSearchProvider ─────────────────────────────────────────────────────

/**
 * Headless search provider backed by Algolia.
 *
 * Talks to the Algolia search client directly via `multipleQueries`, bypassing
 * `react-instantsearch-dom`. The orchestrator-owned `query` prop drives a single
 * `useEffect` that fires one network round-trip; the response is fanned out into
 * the universal results shape via `onResults`.
 *
 * This is intentional: the previous implementation wrapped <InstantSearch> and
 * relied on connector HOCs (connectAutoComplete, connectInfiniteHits, etc.) to
 * subscribe to InstantSearch's internal store. That store emits on every
 * internal state mutation (search start/finish, isSearchStalled flips, Configure
 * dispatches, Index registration, etc.), and each emit re-renders every
 * subscribed connector — saturating the event loop and freezing the page.
 * Calling the search client directly cuts out the entire subscription layer.
 *
 * Props:
 *   query     {string}   Controlled query — set by SearchOrchestrator.
 *   options   {Object}
 *     apiKey           {string}   Algolia search-only API key.
 *     primaryIndex     {string}   Root index ('repos' | 'sets_questions').
 *     secondaryIndexes {string[]} Indexes queried in parallel with the primary.
 *     hitsPerPage      {number|null}
 *     filters          {string}
 *   onResults {Function} Called with { query, suggestions, hits, indexResults, corpus }.
 *
 * The emitted `query` is the query the results were fetched FOR, not the query
 * currently typed. Algolia is a network round-trip, so between a keystroke and
 * the response landing this provider still holds the previous query's hits.
 * SearchOrchestrator compares the two and refuses to build a quality report
 * until they agree — otherwise MiniSearch (synchronous) gets scored against a
 * stale or empty Algolia result set. See `algoliaSettled` in searchReporter.
 */
function AlgoliaSearchProvider({ query, options, onResults }) {
  const {
    apiKey, primaryIndex, secondaryIndexes, hitsPerPage, filters,
  } = options

  // Refs keep the latest callback/query reachable from in-flight promises
  // without forcing them into useEffect deps (which would cause loops).
  const onResultsRef = useRef(onResults)
  onResultsRef.current = onResults
  const queryRef = useRef(query)
  queryRef.current = query

  // Stable list of indexes we query in parallel.
  const secondaryKey = (secondaryIndexes || []).join(',')
  // secondaryKey is the stable string projection of secondaryIndexes used as
  // the memo dep — secondaryIndexes itself is intentionally excluded.
  const allIndexes = useMemo(
    () => [primaryIndex, ...(secondaryIndexes || [])],
    [primaryIndex, secondaryKey],
  )

  // Per-index Algolia response state, kept in ONE state object together with
  // the query it belongs to so the pair can never be observed out of step.
  // Updated atomically when the initial search resolves; mutated by
  // loadMoreForIndex when a Show More click appends a page of hits.
  const [response, setResponse] = useState({ query: null, byIndex: {} })
  const { byIndex: perIndex } = response

  // How many documents Algolia is searching, as opposed to how many matched.
  // Null until the probe below lands, and never inferred from a query's nbHits.
  const [corpus, setCorpus] = useState(null)

  const searchClient = useMemo(() => {
    if (!apiKey) return null
    return getAlgoliaClient(
      process.env.REACT_APP_PLICKERS_ALGOLIA_APP_ID,
      apiKey,
    )
  }, [apiKey])

  // Initial fetch — fires whenever query, indexes, or search params change.
  // Always requests page 0 across all indexes; loadMore handles pagination.
  useEffect(() => {
    if (!searchClient) return undefined
    let cancelled = false

    const queries = allIndexes.map((indexName) => ({
      indexName,
      query,
      params: {
        filters,
        ...(hitsPerPage != null ? { hitsPerPage } : {}),
        page: 0,
      },
    }))

    searchClient.multipleQueries(queries).then((response) => {
      if (cancelled) return
      const next = {}
      const results = (response && response.results) || []
      results.forEach((r, i) => {
        next[allIndexes[i]] = {
          hits: r.hits || [],
          hitCount: r.nbHits || 0,
          page: r.page || 0,
          pageCount: r.nbPages || 0,
        }
      })
      setResponse({ query, byIndex: next })
    }).catch(() => {
      // Swallow errors here — Algolia client logs to console internally and a
      // failed search should not crash the app. The previous results remain
      // in perIndex so the UI does not flash to empty on transient failures.
    })

    return () => { cancelled = true }
  }, [searchClient, query, allIndexes, hitsPerPage, filters])

  // ─── Corpus probe ────────────────────────────────────────────────────────
  //
  // An empty query with `hitsPerPage: 0` returns the size of the filtered index
  // and no hits payload, which is the only number that makes the local index
  // sizes meaningful: comparing what the API says exists, what MiniSearch
  // indexed, and what Algolia is searching separates a failed load from a
  // genuine difference between the two backends.
  //
  // Deliberately keyed on the client, the index list and the filters — never on
  // `query`, which would turn one request per session into one per keystroke.
  // The same filters as the live search, or the two numbers are not comparable.
  //
  // `exhaustiveNbHits` comes back false when Algolia estimated rather than
  // counted, which happens on large indices. It is recorded rather than
  // discarded so a comparison against a soft number can say that it is one.
  useEffect(() => {
    if (!searchClient) return undefined
    let cancelled = false

    const queries = allIndexes.map((indexName) => ({
      indexName,
      query: '',
      params: { filters, hitsPerPage: 0 },
    }))

    searchClient.multipleQueries(queries).then((probe) => {
      if (cancelled) return
      const counts = {}
      let exhaustive = true
      const results = (probe && probe.results) || []
      results.forEach((r, i) => {
        counts[allIndexes[i]] = typeof r.nbHits === 'number' ? r.nbHits : null
        if (r.exhaustiveNbHits === false) exhaustive = false
      })
      setCorpus({ measured: true, exhaustive, counts })
    }).catch(() => {
      if (cancelled) return
      // A failed probe reports null counts rather than zeroes. Zero would read
      // as an empty Algolia index and make every local count look like excess.
      setCorpus({ measured: false, exhaustive: null, counts: {} })
    })

    return () => { cancelled = true }
  }, [searchClient, allIndexes, filters])

  // loadMore: fetches the next page for one index and appends to its hits.
  // Stable identity — only re-created when query/filters/page-size or the
  // searchClient change. We compare queryAtTime via queryRef in the resolver
  // to discard responses for a stale query.
  const loadMoreForIndex = useCallback((indexName) => {
    if (!searchClient) return
    const queryAtTime = queryRef.current
    setResponse((prev) => {
      const current = prev.byIndex[indexName]
      if (!current) return prev
      const nextPage = current.page + 1
      if (nextPage >= current.pageCount) return prev
      searchClient.initIndex(indexName).search(queryAtTime, {
        filters,
        ...(hitsPerPage != null ? { hitsPerPage } : {}),
        page: nextPage,
      }).then((r) => {
        if (queryAtTime !== queryRef.current) return
        setResponse((p) => {
          const cur = p.byIndex[indexName]
          if (!cur) return p
          return {
            ...p,
            byIndex: {
              ...p.byIndex,
              [indexName]: {
                hits: [...cur.hits, ...(r.hits || [])],
                hitCount: r.nbHits || cur.hitCount,
                page: r.page || nextPage,
                pageCount: r.nbPages || cur.pageCount,
              },
            },
          }
        })
      }).catch(() => {})
      return prev
    })
  }, [searchClient, filters, hitsPerPage])

  // Project per-index state into the universal results shape and notify the
  // orchestrator. This effect is the single point that calls onResults — it
  // fires on perIndex changes only, so a stable Algolia response will never
  // re-trigger the orchestrator.
  useEffect(() => {
    const indexResults = makeEmptyIndexResults()
    allIndexes.forEach((indexName) => {
      const data = perIndex[indexName]
      if (!data) return
      indexResults[indexName] = {
        hits: data.hits,
        hitCount: data.hitCount,
        hasMore: (data.page + 1) < data.pageCount,
        loadMore: () => loadMoreForIndex(indexName),
      }
    })

    const suggestions = allIndexes.map((indexName) => ({
      index: indexName,
      hits: (perIndex[indexName] && perIndex[indexName].hits) || [],
    }))

    onResultsRef.current({
      query: response.query,
      suggestions,
      hits: (perIndex[primaryIndex] && perIndex[primaryIndex].hits) || [],
      indexResults,
      corpus,
    })
  }, [response, perIndex, allIndexes, primaryIndex, loadMoreForIndex, corpus])

  return null
}

export default AlgoliaSearchProvider
